import React, { useState } from "react";
import { selezionaLivello } from "../services/api";
import { updateSubscription, upgradeSubscription, addPackageToSubscription, validateSubscriptionCoupon } from '../services/api_subscriptions';
import Swal from "sweetalert2";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { AddShoppingCart } from "@mui/icons-material";
import { calculatePackageCost } from "../services/utils";

const ModalUpdateSubscription = ({
    mode,
    selectedPlan,
    employeeCount,
    newQuantity,
    selectedPrice,
    onClose,
    token,
    duration,
    newPrice,
    onUpdateQuantity,
    subscriptionIdtoUpgrade,
    planUpgrade,
    onSubscriptionChanged,
    activeProSubscription,
    activeBaseSubscription,
    selectedPackages = [],
    customerType,
}) => {
    const [couponCode, setCouponCode] = useState("");
    const [couponError, setCouponError] = useState("");
    const [couponPreview, setCouponPreview] = useState(null);
    const [validatedCouponCode, setValidatedCouponCode] = useState("");
    const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const isNew = mode === "newSubscription";
    const isUpgrade = mode === "upgradeSubscription";
    const isUpdateQuantity = mode === "updateSubscription";
    const isAddPackage = mode === "addPackageToSubscription";
    
    const isIncreasingQuantity = Number(newQuantity?.quantity || 0) > Number(selectedPlan?.quantity || 0);
    const isMonthlyPlan = selectedPlan?.plan?.duration_months === 1;
    const isYearlyPlan = selectedPlan?.plan?.duration_months === 12;

    const ACTIVE_PACKAGE_STATUSES = ["active", "cancel_at_period_end"];
        
    const formatMoney = (value) => Number(value || 0).toFixed(2);
    const formatCents = (value) =>
    (Number(value || 0) / 100).toLocaleString("it-IT", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    const getCouponDiscountLabel = (coupon) => {
        if (!coupon) return "";

        if (Number(coupon.percent_off) > 0) {
            return `${Number(coupon.percent_off).toLocaleString("it-IT")}%`;
        }

        if (Number(coupon.amount_off) > 0) {
            return `€ ${formatCents(coupon.amount_off)}`;
        }

        return "";
    };

    const getCouponDurationLabel = (coupon) => {
        if (!coupon) return "";

        if (coupon.duration === "forever") {
            return "Per sempre";
        }

        if (coupon.duration === "once") {
            return "Una sola volta";
        }

        if (
            coupon.duration === "repeating" &&
            Number(coupon.duration_in_months) > 0
        ) {
            const months = Number(coupon.duration_in_months);

            return months === 1
                ? "1 mese"
                : `${months} mesi`;
        }

        return "";
    };
    
    const modalTitle = isAddPackage
        ? "Riepilogo aggiunta pacchetto"
        : `Riepilogo ${
            isUpdateQuantity || isUpgrade
                ? "Aggiornamento Sottoscrizione"
                : "Nuova Sottoscrizione"
        }`;

    const getPlanBase = (code = "") => {
        if (code.includes("consultala_hr_pro")) return "pro";
        if (code.includes("consultala_hr_base")) return "base";
        return null;
    };

    const getPlanDuration = (code = "") => {
        if (code.endsWith("_year")) return "year";
        if (code.endsWith("_month")) return "month";
        return null;
    };

    const activePackages = activeProSubscription?.packages || [];

    const activePackageNames = activePackages
        .filter((pkg) => ACTIVE_PACKAGE_STATUSES.includes(pkg?.status))
        .map((pkg) => pkg?.plan?.name)
        .filter(Boolean);

    const hasActivePackages = activePackageNames.length > 0;

    const activePlanCode = activeProSubscription?.plan?.code || activeBaseSubscription?.plan?.code || "";
    const targetPlanCode = selectedPlan?.codename || "";

    const activePlanBase = getPlanBase(activePlanCode);
    const targetPlanBase = getPlanBase(targetPlanCode);

    const activePlanDuration = getPlanDuration(activePlanCode);
    
    const targetPlanDuration = selectedPlan?.duration || getPlanDuration(targetPlanCode);

    const selectedPackagesByDuration = selectedPackages.filter((pkg) => {
        if (!targetPlanDuration) return true;
        return pkg.duration === targetPlanDuration;
    });

    const hasSelectedPackages = selectedPackagesByDuration.length > 0;

    const isDowngradeToBase =
        isUpgrade &&
        activePlanBase === "pro" &&
        targetPlanBase === "base" &&
        hasActivePackages;

    const isProDurationChangeWithPackages =
        isUpgrade &&
        activePlanBase === "pro" &&
        targetPlanBase === "pro" &&
        activePlanDuration &&
        targetPlanDuration &&
        activePlanDuration !== targetPlanDuration &&
        hasActivePackages;

    const isBaseToProUpgrade =
        isUpgrade &&
        activePlanBase === "base" &&
        targetPlanBase === "pro";

    const renewalLabel =
        activeProSubscription?.plan?.duration_months === 12 ? "annuale" : "mensile";

    const renewalDate = activeProSubscription?.current_period_end
        ? new Date(activeProSubscription.current_period_end).toLocaleDateString("it-IT")
        : null;

    const hasValidatedCoupon =
        Boolean(validatedCouponCode) &&
        validatedCouponCode === couponCode.trim() &&
        Boolean(couponPreview?.valid);

    const renderPackagePrice = (pkg) => {
        if (!pkg) return null;

        const { amountCost, regularCost, hasPromo } = calculatePackageCost(
            pkg,
            employeeCount,
            customerType
        );

        return (
            <span className="inline-flex flex-wrap items-center justify-end gap-2 text-sm">
                {hasPromo && regularCost !== null && (
                    <>
                        <span className="text-gray-500 line-through">
                            € {formatMoney(regularCost)}
                        </span>

                        <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold text-orange-600">
                            PROMO
                        </span>
                    </>
                )}

                <span className="font-bold text-indigo-700">
                    € {formatMoney(amountCost)}
                    <span className="ml-1 font-semibold">
                        / {duration === "year" ? "anno" : "mese"} + IVA
                    </span>
                </span>

                {pkg?.is_per_seat && (
                    <span className="w-full text-right text-[11px] text-indigo-500">
                        calcolato su {Number(employeeCount || 1)} dipendenti
                    </span>
                )}
            </span>
        );
    };

    const handleValidateCoupon = async () => {
        const normalizedCode = couponCode.trim();

        setCouponError("");
        setCouponPreview(null);
        setValidatedCouponCode("");

        if (!normalizedCode) {
            setCouponError("Inserisci un codice coupon");
            return;
        }

        const packagePriceId =
            selectedPackagesByDuration?.[0]?.id || null;

        setIsValidatingCoupon(true);

        try {
            const response = await validateSubscriptionCoupon(
                token,
                {
                    coupon: normalizedCode,
                    planPriceId: selectedPlan?.id,
                    quantity: Number(employeeCount || 1),
                    packagePriceId:
                        selectedPackagesByDuration?.[0]?.id || null,
                }
            );

            if (!response?.valid) {
                throw new Error(
                    response?.error ||
                    "Coupon non valido"
                );
            }

            setCouponPreview(response);
            setValidatedCouponCode(normalizedCode);
        } catch (error) {
            const message =
                error.response?.data?.error ||
                error.message ||
                "Non è stato possibile verificare il coupon";

            setCouponError(message);
            setCouponPreview(null);
            setValidatedCouponCode("");
        } finally {
            setIsValidatingCoupon(false);
        }
    };

    const handleRemoveCoupon = () => {
        setCouponCode("");
        setCouponError("");
        setCouponPreview(null);
        setValidatedCouponCode("");
    };

    const handleConfirm = async () => {
        setIsLoading(true);
        setCouponError("");

        try {
            if (isNew) {
                const packagePriceId = selectedPackagesByDuration?.[0]?.id || null;

                const response = await selezionaLivello(
                    token,
                    selectedPlan?.id,
                    employeeCount,
                    couponCode || null,
                    packagePriceId
                );

                if (response.url) {
                    onClose();
                    window.location.href = response.url;
                }

                return;
            }

            if (isUpdateQuantity) {
                const subscriptionId = selectedPlan?.id;

                if (!subscriptionId) {
                    throw new Error("ID della subscription non trovato");
                }

                Swal.fire({
                    title: "Aggiornamento in corso...",
                    text: "Attendi mentre aggiorniamo la sottoscrizione.",
                    icon: "info",
                    allowOutsideClick: false,
                    showConfirmButton: false,
                    willOpen: () => Swal.showLoading(),
                });

                await updateSubscription(token, subscriptionId, newQuantity.quantity);

                await Swal.fire({
                    title: "Sottoscrizione aggiornata!",
                    text: `La sottoscrizione è stata aggiornata con successo alla quantità ${newQuantity?.quantity}.`,
                    icon: "success",
                    confirmButtonText: "OK",
                });

                onUpdateQuantity?.(newQuantity.quantity);
                await onSubscriptionChanged?.();
                onClose();
                return;
            }

            if (isUpgrade) {
                if (!subscriptionIdtoUpgrade || !planUpgrade) {
                    throw new Error("ID della subscription o codice del piano non trovato");
                }

                const packagePriceId = selectedPackagesByDuration?.[0]?.id || null;

                Swal.fire({
                    title: "Aggiornamento in corso...",
                    text: "Attendi mentre eseguiamo l'upgrade della sottoscrizione.",
                    icon: "info",
                    allowOutsideClick: false,
                    showConfirmButton: false,
                    willOpen: () => Swal.showLoading(),
                });

                await upgradeSubscription(
                    token,
                    subscriptionIdtoUpgrade,
                    planUpgrade,
                    packagePriceId
                );

                await Swal.fire({
                    title: "Aggiornamento completato!",
                    text: `La sottoscrizione è stata aggiornata al piano ${selectedPlan?.name}.`,
                    icon: "success",
                    confirmButtonText: "OK",
                });

                await onSubscriptionChanged?.();
                onClose();
                return;
            }

            if (isAddPackage) {
                const packagePriceId = selectedPlan?.id;

                if (!packagePriceId) {
                    throw new Error("ID pacchetto non trovato");
                }

                Swal.fire({
                    title: "Aggiunta pacchetto in corso...",
                    text: "Attendi mentre aggiorniamo la sottoscrizione.",
                    icon: "info",
                    allowOutsideClick: false,
                    showConfirmButton: false,
                    willOpen: () => Swal.showLoading(),
                });

                await addPackageToSubscription(token, packagePriceId);

                await Swal.fire({
                    title: "Pacchetto aggiunto!",
                    text: `Il pacchetto ${selectedPlan?.name} è stato aggiunto alla sottoscrizione.`,
                    icon: "success",
                    confirmButtonText: "OK",
                });

                await onSubscriptionChanged?.();
                onClose();
            }
        } catch (error) {
            if (error.response?.data?.error?.includes("No such coupon")) {
                setCouponError("Il coupon inserito non è valido");
                return;
            }

            const errorMessages = error.response?.data
                ? Object.values(error.response.data).flat().join(", ")
                : "Si è verificato un errore durante il salvataggio.";

            Swal.fire({
                icon: "error",
                title: "Oops...",
                text: errorMessages,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const confirmLabel = isLoading
        ? "Caricamento..."
        : isUpgrade
        ? "Conferma cambio piano"
        : isAddPackage
        ? "Conferma aggiunta"
        : "Conferma";

    const InfoCard = ({ title, children }) => (
        <div className="overflow-hidden rounded-2xl border border-indigo-100 bg-white shadow-sm">
            <div className="bg-indigo-50 px-4 py-3">
                <p className="text-sm font-bold uppercase tracking-wide text-indigo-700">
                    {title}
                </p>
            </div>
            <div className="divide-y divide-gray-100">{children}</div>
        </div>
    );

    const SummaryRow = ({ label, children }) => (
        <div className="flex items-center justify-between gap-4 px-4 py-3">
            <span className="text-sm font-medium text-gray-500">{label}</span>
            <div className="text-right text-sm font-bold text-indigo-800">
                {children || "-"}
            </div>
        </div>
    );

    const PriceCard = ({ title, subtitle, amount, children }) => (
        <div className="rounded-2xl border border-indigo-200 bg-indigo-50/60 p-4 shadow-sm">
            <div className="flex items-center justify-between gap-4">
                <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-indigo-600">
                        {title}
                    </p>
                    {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
                </div>

                <div className="text-right">
                    <p className="text-xl font-extrabold text-indigo-800">
                        € {formatMoney(amount)} + IVA
                    </p>
                    {!isUpdateQuantity && (
                        <p className="text-xs text-gray-500">
                            / {duration === "year" ? "anno" : "mese"}
                        </p>
                    )}
                </div>
            </div>

            {children}
        </div>
    );

    const NoticeBox = ({ variant = "orange", title, children }) => {
        const styles = {
            orange: "border-orange-300 bg-orange-50 text-orange-700",
            red: "border-red-200 bg-red-50 text-red-700",
            amber: "border-amber-200 bg-amber-50 text-amber-700",
            gray: "border-gray-100 bg-gray-50 text-gray-600",
        };

        return (
            <div className={`rounded-xl border p-4 text-sm ${styles[variant]}`}>
                {title && <p className="font-bold">{title}</p>}
                <div className={title ? "mt-2" : ""}>{children}</div>
            </div>
        );
    };

    const renderPackageDetail = (packages) => (
        <div className="mt-4 border-t border-indigo-100 pt-4">
            <p className="mb-2 text-xs font-bold uppercase tracking-wide text-indigo-700">
                Dettaglio pacchetti
            </p>

            <div className="space-y-2">
                {packages.map((pkg) => (
                    <div key={pkg.id} className="rounded-xl bg-white px-3 py-3 shadow-sm">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <span className="text-sm font-bold text-indigo-800">
                                {pkg.name}
                            </span>

                            <span className="text-right">{renderPackagePrice(pkg)}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    const couponDiscountLabel = getCouponDiscountLabel(couponPreview);

    const couponDurationLabel = getCouponDurationLabel(couponPreview);

    return (
        <div
            className="fixed inset-0 z-[999] flex items-start justify-center overflow-y-auto bg-black/50 px-4 py-6 sm:py-10 "
            style={{ zIndex: "999" }}
        >
            <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
                <div className="p-4
                    bg-white/60 backdrop-blur
                    border border-indigo-100 shadow-sm
                    before:absolute before:inset-0 before:-z-10
                    before:bg-gradient-to-r before:from-indigo-300 before:via-sky-300 before:to-purple-300
                    before:opacity-30"
                >
                    <div className="flex flex-col items-center">
                        <span className="py-1 text-[11px] font-bold uppercase tracking-wider text-indigo-700">CONSULTALA HR</span>
                        <div className="rounded-full bg-white/15 px-3 text-md font-bold uppercase tracking-wider text-indigo-700">
                      
                            {modalTitle}
                        </div>
                    </div>
                </div>
                <hr className="h-px border-0 bg-indigo-300 z-10" />

                <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
                    {isUpgrade && (
                        <NoticeBox variant="orange">
                            <div className="text-center">
                                <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-orange-500" />
                                <p className="mt-2 text-lg font-bold">
                                    Attenzione: stai cambiando piano
                                </p>
                                <p className="mt-1">
                                    La modifica avrà effetto immediato. Verifica attentamente i dettagli prima di confermare.
                                </p>
                            </div>
                        </NoticeBox>
                    )}

                    {isDowngradeToBase && (
                        <NoticeBox
                            variant="red"
                            title="Passando al piano Base verranno disattivati anche questi pacchetti:"
                        >
                            <ul className="list-disc pl-5">
                                {activePackageNames.map((name) => (
                                    <li key={name}>{name}</li>
                                ))}
                            </ul>
                        </NoticeBox>
                    )}

                    {isProDurationChangeWithPackages && (
                        <NoticeBox
                            variant="amber"
                            title="Stai modificando la periodicità del piano Pro."
                        >
                            <p>I pacchetti attivi seguiranno la nuova periodicità di rinnovo:</p>
                            <ul className="mt-2 list-disc pl-5">
                                {activePackageNames.map((name) => (
                                    <li key={name}>{name}</li>
                                ))}
                            </ul>
                        </NoticeBox>
                    )}

                    {isAddPackage && (
                        <div className="overflow-hidden rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-indigo-100 shadow-sm">
                            <div className="flex flex-col items-center px-6 pt-6">
                                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-indigo-100">
                                    <AddShoppingCart className="h-7 w-7 text-indigo-600" />
                                </div>

                                <h3 className="mt-3 text-xl font-bold text-indigo-800">
                                    Conferma aggiunta pacchetto
                                </h3>
                            </div>

                            <div className="px-6 py-5 text-justify">
                                <div className="rounded-xl border border-indigo-100 bg-white p-4">
                                    <p className="text-sm leading-6 text-gray-700">
                                        Il costo verrà addebitato immediatamente, alla conferma di aggiunta, sul metodo di pagamento salvato, in proporzione al periodo residuo fino al prossimo rinnovo della sottoscrizione <strong>HR Pro</strong>. Rinnovo <strong>{renewalLabel}</strong>
                                        {renewalDate ? <> previsto per il <strong>{renewalDate}</strong></> : null}.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {(isNew || isUpgrade) && (
                        <>
                            <InfoCard title="Riepilogo configurazione">
                                <SummaryRow label="Piano">
                                    <span className="uppercase">{selectedPlan?.name}</span>
                                </SummaryRow>

                                {hasSelectedPackages && (
                                    <SummaryRow
                                        label={isUpgrade ? "Pacchetto collegato" : "Pacchetti selezionati"}
                                    >
                                        <div className="flex flex-wrap justify-end gap-2">
                                            {selectedPackagesByDuration.map((pkg) => (
                                                <span
                                                    key={pkg.id}
                                                    className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-bold text-indigo-700"
                                                >
                                                    {pkg.name}
                                                </span>
                                            ))}
                                        </div>
                                    </SummaryRow>
                                )}

                                <div className="grid grid-cols-2 divide-x divide-gray-100">
                                    <div className="px-4 py-3">
                                        <p className="text-xs font-medium uppercase text-gray-400">
                                            Dipendenti
                                        </p>
                                        <p className="mt-1 text-lg font-bold text-indigo-800">
                                            {employeeCount}
                                        </p>
                                    </div>

                                    <div className="px-4 py-3">
                                        <p className="text-xs font-medium uppercase text-gray-400">
                                            Rinnovo
                                        </p>
                                        <p className="mt-1 text-lg font-bold text-indigo-800">
                                            {duration === "year" ? "Annuale" : "Mensile"}
                                        </p>
                                    </div>
                                </div>
                            </InfoCard>
                            {!(hasValidatedCoupon && couponPreview) && (
                                <PriceCard
                                    title="Totale"
                                    subtitle={
                                        hasSelectedPackages
                                            ? "Piano + pacchetti selezionati"
                                            : "Prezzo piano"
                                    }
                                    amount={selectedPrice || 0}
                                >
                                    {hasSelectedPackages && renderPackageDetail(selectedPackagesByDuration)}
                                </PriceCard>
                            )}

                            {isUpgrade && (
                                <NoticeBox variant="gray">
                                    {isBaseToProUpgrade ? (
                                        <>
                                            <p>
                                                La modifica dal piano Base al piano Pro avrà effetto immediato.
                                            </p>
                                            <p className="mt-1">
                                                L'importo della differenza proporzionale relativa alla modifica del piano, calcolato fino alla data prevista di rinnovo, verrà addebitato immediatamente sul metodo di pagamento salvato.
                                            </p>
                                            <p className="mt-1">
                                                Eventuali coupon esistenti saranno applicati e debiti o crediti saranno inclusi nel calcolo.
                                            </p>
                                        </>
                                    ) : activePlanDuration === "month" && targetPlanDuration === "year" ? (
                                        <>
                                            <p>
                                                La modifica del piano da mensile ad annuale avrà effetto immediato.
                                            </p>
                                            <p className="mt-1">
                                                Verrà addebitato l'importo del nuovo piano annuale, al netto dell’eventuale residuo già pagato per il piano mensile che viene sostituito.
                                            </p>
                                            <p className="mt-1">
                                                Eventuali coupon esistenti saranno applicati e debiti o crediti saranno inclusi nel calcolo.
                                            </p>
                                        </>
                                    ) : (
                                    <>
                                        <p>
                                            Il prezzo indicato verrà addebitato dalla prossima fattura prevista alla data del rinnovo.
                                        </p>
                                        <p className="mt-1">
                                            La modifica del piano avrà effetto immediato. Eventuali coupon esistenti saranno applicati e debiti o crediti saranno inclusi nella prossima fattura.
                                        </p>
                                    </>
                                    )}
                                </NoticeBox>
                            )}
                        </>
                    )}

                    {isAddPackage && (
                        <>
                            <InfoCard title="Riepilogo pacchetto">
                                <SummaryRow label="Pacchetto">
                                    <span className="uppercase">{selectedPlan?.name}</span>
                                </SummaryRow>

                                <SummaryRow label="Rinnovo piano">
                                    {renewalLabel}
                                </SummaryRow>
                            </InfoCard>

                            <PriceCard
                                title="Prezzo pacchetto"
                                subtitle="Pacchetto aggiunto alla sottoscrizione corrente"
                                amount={
                                    calculatePackageCost(
                                        selectedPlan,
                                        employeeCount,
                                        customerType
                                    )?.amountCost
                                }
                            >
                                <div className="mt-4 border-t border-indigo-100 pt-4 text-right">
                                    {renderPackagePrice(selectedPlan)}
                                </div>
                                <div className="text-xs mt-2 text-gray-500">
                                    Eventuali <span className="font-bold">coupon</span> già presenti sulla sottoscrizione attiva verranno <span className="font-bold">applicati</span> sul pacchetto in fase di pagamento
                                </div>
                            </PriceCard>
                        </>
                    )}

                    {isUpdateQuantity && (
                        <>
                            <InfoCard title="Riepilogo modifica licenze">
                                <SummaryRow label="Piano">
                                    {selectedPlan?.plan?.name || "Nessun piano trovato"}
                                </SummaryRow>

                                <div className="grid grid-cols-2 divide-x divide-gray-100">
                                    <div className="px-4 py-3">
                                        <p className="text-xs font-medium uppercase text-gray-400">
                                            Licenze attuali
                                        </p>
                                        <p className="mt-1 text-lg font-bold text-indigo-800">
                                            {selectedPlan?.quantity}
                                        </p>
                                    </div>

                                    <div className="px-4 py-3">
                                        <p className="text-xs font-medium uppercase text-gray-400">
                                            Nuove licenze
                                        </p>
                                        <p className="mt-1 text-lg font-bold text-indigo-800">
                                            {newQuantity?.quantity}
                                        </p>
                                    </div>
                                </div>

                                <SummaryRow label="Rinnovo">
                                    {selectedPlan?.plan?.duration_months === 1 ? "Mensile" : "Annuale"}
                                </SummaryRow>
                            </InfoCard>

                            <PriceCard
                                title="Nuovo prezzo"
                                subtitle="Prezzo calcolato sulla nuova quantità"
                                amount={newPrice?.discounted || 0}
                            />

                            <NoticeBox variant="gray">
                                {selectedPlan?.quantity > newQuantity?.quantity ? (
                                    <>
                                        <p>
                                            Il prezzo delle licenze eliminate e già pagate verrà sottratto nella prossima fattura.
                                        </p>
                                        <p className="mt-1">
                                            Eventuali coupon già presenti verranno applicati.
                                        </p>
                                    </>
                                ) : isIncreasingQuantity && isMonthlyPlan ? (
                                    <>
                                        <p>
                                            L'importo proporzionale relativo alle licenze aggiunte verrà addebitato nella prossima fattura prevista alla data del rinnovo.
                                        </p>
                                        <p className="mt-1">
                                            Eventuali coupon già presenti verranno applicati.
                                        </p>
                                    </>
                                ) : isIncreasingQuantity && isYearlyPlan ? (
                                    <>
                                        <p>
                                            L'importo proporzionale relativo alle licenze aggiunte verrà addebitato subito sul metodo di pagamento salvato.
                                        </p>
                                        <p className="mt-1">
                                            Il calcolo sarà effettuato in proporzione ai giorni restanti fino al prossimo rinnovo. Eventuali coupon già presenti verranno applicati.
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <p>
                                            Il prezzo indicato verrà addebitato dalla prossima fattura prevista alla data del rinnovo.
                                        </p>
                                        <p className="mt-1">
                                            Eventuali coupon già presenti verranno applicati.
                                        </p>
                                    </>
                                )}
                            </NoticeBox>
                        </>
                    )}

                    {isNew && (
                        <div className="mt-4 overflow-hidden rounded-2xl border border-indigo-200 bg-white shadow-sm">
                            <div className="border-b border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-purple-50 px-4 py-4">
                                <div className="flex items-center gap-3">
                                    <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-indigo-100 text-lg font-extrabold text-indigo-700">
                                        %
                                    </span>

                                    <div>
                                        <p className="text-sm font-bold text-indigo-900">
                                            Hai un coupon?
                                        </p>

                                        <p className="mt-0.5 text-xs text-gray-500">
                                            Inserisci il codice prima di confermare e verifica subito il prezzo aggiornato
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-3 p-4">
                                <div className="flex flex-col gap-2 sm:flex-row">
                                    <input
                                        type="text"
                                        value={couponCode}
                                        onChange={(event) => {
                                            const value = event.target.value;

                                            setCouponCode(value);
                                            setCouponError("");

                                            if (
                                                validatedCouponCode &&
                                                value.trim() !== validatedCouponCode
                                            ) {
                                                setCouponPreview(null);
                                                setValidatedCouponCode("");
                                            }
                                        }}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter") {
                                                event.preventDefault();
                                                handleValidateCoupon();
                                            }
                                        }}
                                        disabled={isValidatingCoupon || isLoading}
                                        className={`min-w-0 flex-1 rounded-xl border bg-white px-3 py-2.5 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:ring-2 ${
                                            couponError
                                                ? "border-orange-300 focus:border-orange-400 focus:ring-orange-100"
                                                : hasValidatedCoupon
                                                ? "border-indigo-400 bg-indigo-50/30 focus:border-indigo-500 focus:ring-indigo-100"
                                                : "border-indigo-100 focus:border-indigo-500 focus:ring-indigo-100"
                                        }`}
                                        placeholder="Inserisci il codice coupon"
                                    />

                                    <button
                                        type="button"
                                        onClick={handleValidateCoupon}
                                        disabled={
                                            isValidatingCoupon ||
                                            isLoading ||
                                            !couponCode.trim()
                                        }
                                        className="inline-flex min-w-28 items-center justify-center rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        {isValidatingCoupon
                                            ? "Verifica..."
                                            : hasValidatedCoupon
                                            ? "Verificato"
                                            : "Applica"}
                                    </button>
                                </div>

                                {customerType === "consultant" && !(hasValidatedCoupon && couponPreview) && (
                                    <div className="rounded-xl border border-purple-100 bg-purple-50/60 px-3 py-2.5">
                                        <p className="text-xs leading-5 text-gray-600">
                                            È già attivo uno sconto consulente del{" "}
                                            <strong className="text-purple-700">30%</strong>.
                                            Un eventuale coupon inserito sostituirà lo sconto
                                            attualmente applicato.
                                        </p>
                                    </div>
                                )}

                                {couponError && (
                                    <div className="flex items-start gap-2 rounded-xl border border-orange-200 bg-orange-50 px-3 py-2.5">
                                        <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-none text-orange-500" />

                                        <p className="text-xs font-medium leading-5 text-orange-700">
                                            {couponError}
                                        </p>
                                    </div>
                                )}

                                {hasValidatedCoupon && couponPreview && (
                                    <div className="overflow-hidden rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-purple-50">
                                        <div className="flex items-center justify-between gap-4 border-b border-indigo-100 px-4 py-3">
                                            <div>
                                                <p className="text-xs font-bold uppercase tracking-wide text-indigo-700">
                                                    Coupon applicato
                                                </p>

                                                <p className="mt-0.5 text-xs text-gray-500">
                                                    Il prezzo è stato aggiornato correttamente
                                                </p>
                                            </div>

                                            <div className="flex flex-wrap items-center justify-end gap-2">
                                                <span className="rounded-full bg-indigo-100 px-3 py-1 text-[11px] font-bold uppercase text-indigo-700">
                                                    {validatedCouponCode}
                                                </span>

                                                {couponDiscountLabel && (
                                                    <span className="rounded-full bg-purple-100 px-3 py-1 text-[11px] font-bold text-purple-700">
                                                        {couponDiscountLabel}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="space-y-3 px-4 py-4">
                                            <div className="flex items-center justify-between gap-4 text-sm">
                                                <span className="text-gray-500">
                                                    Prezzo originale
                                                </span>

                                                <span className="font-semibold text-gray-700">
                                                    € {formatCents(couponPreview.subtotal_cents)}
                                                </span>
                                            </div>

                                            <div className="flex items-center justify-between gap-4 text-sm">
                                                <span className="text-gray-500">
                                                    Sconto coupon
                                                </span>

                                                <span className="font-bold text-purple-700">
                                                    - € {formatCents(couponPreview.discount_cents)}
                                                </span>
                                            </div>

                                            {couponDurationLabel && (
                                                <div className="flex items-center gap-2 text-sm">
                                                    <span className="text-gray-500">
                                                        Durata sconto
                                                    </span>

                                                    <span className="rounded-full bg-indigo-100 px-2.5 py-1 text-xs font-bold text-indigo-700">
                                                        {couponDurationLabel}
                                                    </span>
                                                </div>
                                            )}

                                            <div className="flex items-end justify-between gap-4 border-t border-indigo-100 pt-3">
                                                <div>
                                                    <p className="text-sm font-bold text-indigo-900">
                                                        Totale pagamento
                                                    </p>
                                                </div>

                                                <div className="text-right">
                                                    <p className="text-xl font-extrabold text-indigo-800">
                                                        € {formatCents(couponPreview.amount_due_cents)}
                                                    </p>

                                                    <p className="text-[11px] font-medium text-gray-500">
                                                        IVA inclusa
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex justify-end border-t border-indigo-100 bg-white/60 px-4 py-3">
                                            <button
                                                type="button"
                                                onClick={handleRemoveCoupon}
                                                disabled={isLoading || isValidatingCoupon}
                                                className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-500 transition hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                <span aria-hidden="true">×</span>
                                                Rimuovi coupon
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="shrink-0 border-t bg-white p-4">
                    <div className="flex justify-center">
                        <button
                            className={`me-3 inline-flex items-center justify-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 whitespace-nowrap ${
                                isUpgrade ? "w-56" : isAddPackage ? "w-40" : "w-28"
                            } ${isLoading || isValidatingCoupon ? "cursor-not-allowed opacity-50" : ""}`}
                            onClick={handleConfirm}
                            disabled={isLoading || isValidatingCoupon}
                        >
                            {confirmLabel}
                        </button>

                        <button
                            className="w-28 rounded-md border px-3 py-2 text-center text-sm font-semibold text-gray-800 shadow-sm hover:bg-gray-100"
                            onClick={onClose}
                            disabled={isLoading || isValidatingCoupon}
                        >
                            Annulla
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModalUpdateSubscription;
